// Pure helper for the template dependencies graph. The endpoint returns a
// bidirectional structure: each node carries `dependees` (templates it depends
// on) and `dependents` (templates that depend on it). The root sits in the
// centre, dependees fan left and dependents fan right. It draws as an ECharts
// graph series, the only series type that draws arrowheads. This builds the
// positioned nodes and directed links for that series.
//
// Layout is a simple mirrored tidy tree: each side is laid out independently
// (leaves take sequential vertical slots, parents centre over their children),
// then the two sides are aligned so the root sits level between them. Every
// arrow points left to right, from a template to the template that depends on it.

export interface DependencyNode {
  name: string
  tooltip?: string
  dependees?: DependencyNode[]
  dependents?: DependencyNode[]
}

export type LabelPosition = 'left' | 'right' | 'top'

export interface GraphNode {
  id: string
  name: string
  tooltip?: string
  // Abstract coordinates: x is signed depth (dependees < 0, root 0, dependents
  // > 0); y is a vertical ordering. The behaviour scales these to pixels.
  x: number
  y: number
  labelPosition: LabelPosition
}

export interface GraphLink {
  source: string
  target: string
}

export interface DependencyGraph {
  nodes: GraphNode[]
  links: GraphLink[]
}

// The ECharts series box. Its size equals the graph's own size, so the graph
// draws at a scale of 1.
export interface GraphLayout {
  left: 'center'
  top: number
  width: number
  height: number
}

export interface GraphView {
  height: number
  layout?: GraphLayout
}

export const ROOT_ID = '0'

// Pixels between depth levels (x) and between sibling rows (y), plus canvas
// padding around the laid-out graph.
export const X_STEP = 220
export const Y_STEP = 70
export const PADDING = 48
const MIN_HEIGHT = 240

const mean = (values: number[]): number =>
  values.reduce((sum, value) => sum + value, 0) / values.length

export function toDependencyGraph(root: DependencyNode): DependencyGraph {
  const nodes: GraphNode[] = []
  const links: GraphLink[] = []

  const buildSide = (
    node: DependencyNode,
    id: string,
    depth: number,
    side: 1 | -1,
    parentId: string,
    nextLeaf: () => number
  ): number => {
    const children = (side > 0 ? node.dependents : node.dependees) ?? []

    let y: number
    if (children.length === 0) {
      y = nextLeaf()
    } else {
      const childYs = children.map((child, index) =>
        buildSide(child, `${id}.${index}`, depth + 1, side, id, nextLeaf)
      )
      y = (childYs[0] + childYs[childYs.length - 1]) / 2
    }

    nodes.push({
      id,
      name: node.name,
      tooltip: node.tooltip,
      x: depth * side,
      y,
      labelPosition: side > 0 ? 'right' : 'left',
    })

    // Arrow toward the higher-x endpoint: away from root for dependents, toward
    // root for dependees, so every arrow reads left to right.
    links.push(
      side > 0
        ? { source: parentId, target: id }
        : { source: id, target: parentId }
    )

    return y
  }

  const makeCursor = (): (() => number) => {
    let next = 0
    return () => next++
  }

  const rightChildren = root.dependents ?? []
  const rightCursor = makeCursor()
  const rightYs = rightChildren.map((child, index) =>
    buildSide(child, `${ROOT_ID}.r${index}`, 1, 1, ROOT_ID, rightCursor)
  )

  const leftChildren = root.dependees ?? []
  const leftCursor = makeCursor()
  const leftStartIndex = nodes.length
  const leftYs = leftChildren.map((child, index) =>
    buildSide(child, `${ROOT_ID}.l${index}`, 1, -1, ROOT_ID, leftCursor)
  )

  // Align the sides so the root is centred between its left and right children.
  const rightMean = rightYs.length ? mean(rightYs) : null
  const leftMean = leftYs.length ? mean(leftYs) : null

  if (rightMean !== null && leftMean !== null) {
    const delta = rightMean - leftMean
    for (let i = leftStartIndex; i < nodes.length; i++) nodes[i].y += delta
  }

  const rootY = rightMean ?? leftMean ?? 0

  nodes.push({
    id: ROOT_ID,
    name: root.name,
    tooltip: root.tooltip,
    x: 0,
    y: rootY,
    labelPosition: 'top',
  })

  return { nodes, links }
}

export function calculateGraphView({
  width,
  height,
  rootY,
  maxHeight,
}: {
  width: number
  height: number
  rootY: number
  maxHeight: number
}): GraphView {
  const naturalHeight = Math.max(height + PADDING * 2, MIN_HEIGHT)

  if (naturalHeight <= maxHeight) {
    return { height: naturalHeight }
  }

  return {
    height: maxHeight,
    layout: { left: 'center', top: maxHeight / 2 - rootY, width, height },
  }
}
